import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import { WorkoutFeedViewType } from '../utils';
import WorkoutProgressionBadge from '../WorkoutProgressionBadge';
import {
  WorkoutHealthStats,
  WorkoutHealthDetails,
} from '../WorkoutHealth';
import { DetailsSection, DetailsTitle } from '../WorkoutHealth/styles';

import {
  Container,
  ContentRow,
  BadgeGroup,
  ItemContainer,
  Column,
  StyledTitle,
  WorkoutItem,
  WorkoutName,
  WorkoutStatusContainer,
  CompletedIcon,
  IncompleteIcon,
  Summary,
  ExpandLabel,
  ProgressionSection,
  WorkoutTitle,
  ExerciseProgressionRow,
  ExerciseName,
  ExerciseDivider,
  WorkoutTitleDot,
} from './styles';
import texts from './texts.json';

const WorkoutDayPanel = ({
  workoutList,
  dayName,
  progressionByWorkoutTitle,
  dayDate,
  onProgressionShown,
  healthByWorkoutKey,
  workoutKeys,
  maxHeartRate,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedHealthKeys, setExpandedHealthKeys] = useState([]);

  const toggleHealthDetails = useCallback((key) => {
    setExpandedHealthKeys((keys) => (keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]));
  }, []);

  // The client's own activities: watch-only ones, and self-assigned workouts once their health data loads.
  const healthActivities = useMemo(() => [
    ...(workoutList[WorkoutFeedViewType.TRACKED_ACTIVITY] ?? []),
    ...(workoutList[WorkoutFeedViewType.SELF_ASSIGNED_WORKOUT] ?? [])
      .filter((workout) => healthByWorkoutKey.has(workoutKeys.get(workout))),
  ], [workoutList, healthByWorkoutKey, workoutKeys]);

  const { title, summaryText } = useMemo(() => {
    let summary = '';
    let titleText = dayName.toUpperCase();
    const assignedWorkoutCount = workoutList[WorkoutFeedViewType.COACH_ASSIGNED_WORKOUT].length;
    const customWorkoutCount = workoutList[WorkoutFeedViewType.CUSTOM_WORKOUT].length;
    const healthActivityCount = healthActivities.length;
    if (assignedWorkoutCount) {
      const completedCount = workoutList[WorkoutFeedViewType.COACH_ASSIGNED_WORKOUT]
        .filter((workout) => !!workout.isCompleted)
        .length;
      summary = `•${summary}${completedCount}/${assignedWorkoutCount} ${texts.workouts}\n`;
    }
    if (customWorkoutCount) {
      summary = `${summary}•${customWorkoutCount} ${texts.customActivities}`;
    }
    if (healthActivityCount) {
      summary = `${summary}${customWorkoutCount ? '\n' : ''}•${healthActivityCount} ${texts.healthActivities}`;
    }
    if (!customWorkoutCount && !assignedWorkoutCount && !healthActivityCount) {
      titleText = `${titleText} - ${texts.restDay}`;
    }
    return { title: titleText, summaryText: summary };
  }, [workoutList, dayName, healthActivities]);

  const hasWorkouts = useMemo(() => (
    workoutList[WorkoutFeedViewType.COACH_ASSIGNED_WORKOUT].length
    || workoutList[WorkoutFeedViewType.CUSTOM_WORKOUT].length
    || healthActivities.length
  ), [workoutList, healthActivities]);

  const renderWorkoutItem = (workout) => {
    const healthKey = workoutKeys.get(workout);
    const healthActivity = healthByWorkoutKey.get(healthKey);
    return (
      <React.Fragment key={healthKey}>
        <WorkoutItem>
          <WorkoutStatusContainer>
            {/* health-only rows are always finished workouts */}
            {workout.isCompleted || workout.type === WorkoutFeedViewType.TRACKED_ACTIVITY
              ? <CompletedIcon />
              : <IncompleteIcon />}
          </WorkoutStatusContainer>
          <WorkoutName>{workout.title}</WorkoutName>
        </WorkoutItem>
        {!!healthActivity && (
          <WorkoutHealthStats
            activity={healthActivity}
            isExpanded={expandedHealthKeys.includes(healthKey)}
            onToggle={() => toggleHealthDetails(healthKey)}
          />
        )}
      </React.Fragment>
    );
  };

  const expandedHealthWorkouts = [
    ...workoutList[WorkoutFeedViewType.COACH_ASSIGNED_WORKOUT],
    ...healthActivities,
  ].filter((workout) => expandedHealthKeys.includes(workoutKeys.get(workout))
    && healthByWorkoutKey.has(workoutKeys.get(workout)));

  const titleKey = (w) => `${(w.title ?? '').trim()}|${dayDate}`;

  const workoutsWithProgression = useMemo(() => [
    ...workoutList[WorkoutFeedViewType.COACH_ASSIGNED_WORKOUT],
    ...workoutList[WorkoutFeedViewType.CUSTOM_WORKOUT],
  ].filter((w) => {
    const key = `${(w.title ?? '').trim()}|${dayDate}`;
    return progressionByWorkoutTitle[key]?.length > 0;
  }),
  [workoutList, progressionByWorkoutTitle, dayDate]);

  const hasProgression = workoutsWithProgression.length > 0;

  useEffect(() => {
    if (isExpanded && hasProgression) {
      onProgressionShown?.();
    }
  }, [isExpanded, hasProgression, onProgressionShown]);

  return (
    <Container>
      <ContentRow
        $hasProgression={hasProgression}
        onClick={hasProgression ? () => setIsExpanded((prev) => !prev) : undefined}
        role={hasProgression ? 'button' : undefined}
        tabIndex={hasProgression ? 0 : undefined}
        onKeyDown={hasProgression ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded((prev) => !prev);
          }
        } : undefined}
        aria-expanded={hasProgression ? isExpanded : undefined}
      >
        <ItemContainer>
          <StyledTitle $hasWorkouts={hasWorkouts}>{title}</StyledTitle>
          <Summary>{summaryText}</Summary>
        </ItemContainer>

        <Column>
          {!!workoutList[WorkoutFeedViewType.COACH_ASSIGNED_WORKOUT].length && (
            <ItemContainer>
              <StyledTitle $hasWorkouts={hasWorkouts}>{texts.workouts}</StyledTitle>
              {workoutList[WorkoutFeedViewType.COACH_ASSIGNED_WORKOUT].map(renderWorkoutItem)}
            </ItemContainer>
          )}
          {!!workoutList[WorkoutFeedViewType.CUSTOM_WORKOUT].length && (
            <ItemContainer>
              <StyledTitle $hasWorkouts={hasWorkouts}>{texts.customActivities}</StyledTitle>
              {workoutList[WorkoutFeedViewType.CUSTOM_WORKOUT].map((workout) => (
                <WorkoutItem key={workout.id}>
                  <WorkoutStatusContainer>
                    {workout.isCompleted ? <CompletedIcon /> : <IncompleteIcon />}
                  </WorkoutStatusContainer>
                  <WorkoutName>{workout.title}</WorkoutName>
                </WorkoutItem>
              ))}
            </ItemContainer>
          )}
        </Column>
        <Column>
          {!!healthActivities.length && (
            <ItemContainer>
              <StyledTitle $hasWorkouts={hasWorkouts}>{texts.healthActivities}</StyledTitle>
              {healthActivities.map(renderWorkoutItem)}
            </ItemContainer>
          )}
        </Column>
        {hasProgression && (
          <ExpandLabel $isExpanded={isExpanded}>
            {isExpanded ? texts.collapse : texts.expand}
          </ExpandLabel>
        )}
      </ContentRow>

      {expandedHealthWorkouts.map((workout) => {
        const healthKey = workoutKeys.get(workout);
        return (
          <DetailsSection key={`health-${healthKey}`}>
            <DetailsTitle>{workout.title}</DetailsTitle>
            <WorkoutHealthDetails activity={healthByWorkoutKey.get(healthKey)} maxHeartRate={maxHeartRate} />
          </DetailsSection>
        );
      })}

      {hasProgression && isExpanded && (
        <ProgressionSection>
          {workoutsWithProgression.map((workout) => {
            const exercises = progressionByWorkoutTitle[titleKey(workout)];
            return (
              <div key={`progression-${workout.id}`}>
                <WorkoutTitle>
                  <WorkoutTitleDot />
                  {workout.title}
                </WorkoutTitle>
                {exercises.map((exercise, idx) => (
                  <React.Fragment key={exercise.name}>
                    <ExerciseProgressionRow>
                      <ExerciseName>{exercise.name}</ExerciseName>
                      <BadgeGroup>
                        {(exercise.badges ?? []).map((badge) => (
                          <WorkoutProgressionBadge
                            key={`${exercise.name}-${badge.metric}`}
                            metric={badge.metric}
                            previousValue={badge.previousValue}
                            currentValue={badge.currentValue}
                            unit={badge.unit}
                          />
                        ))}
                      </BadgeGroup>
                    </ExerciseProgressionRow>
                    {idx < exercises.length - 1 && <ExerciseDivider />}
                  </React.Fragment>
                ))}
              </div>
            );
          })}
        </ProgressionSection>
      )}
    </Container>
  );
};

WorkoutDayPanel.propTypes = {
  workoutList: PropTypes.object.isRequired,
  dayName: PropTypes.string.isRequired,
  progressionByWorkoutTitle: PropTypes.object,
  dayDate: PropTypes.string,
  onProgressionShown: PropTypes.func,
  healthByWorkoutKey: PropTypes.instanceOf(Map),
  workoutKeys: PropTypes.instanceOf(Map),
  maxHeartRate: PropTypes.number,
};

WorkoutDayPanel.defaultProps = {
  progressionByWorkoutTitle: {},
  dayDate: '',
  onProgressionShown: null,
  healthByWorkoutKey: new Map(),
  workoutKeys: new Map(),
  maxHeartRate: 190,
};

export default WorkoutDayPanel;
